"use client";

import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  CalendarDays,
  MapPin,
  MessageCircle,
  Ticket,
  Users,
} from "lucide-react";
import Link from "@/components/ui/link";

/**
 * Serialisable twin of `MemberNextSteps` (`lib/tier/member-next-steps.ts`),
 * for the client boundary.
 */
export type MemberNextStepsData = {
  chat: { id: number; name: string } | null;
  upcomingEventCount: number;
  venueCount: number;
  memberCount: number;
};

type Props = {
  hiiveName: string;
  hiiveSlug: string;
  nextSteps: MemberNextStepsData | null;
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * The way out of "Membership Active".
 *
 * This screen is the end of the join flow, and it used to be literally the end:
 * a heading, a sentence, and no way forward that was not the back button. The
 * person reading it has just spent money or waited for an approval, which is
 * the most willing they will ever be — so the page owes them the rooms their
 * membership just opened, not a receipt.
 *
 * Every tile here is gated on the thing existing. "View events" on a hiive with
 * no events is a worse dead end than no button at all, because it costs a
 * navigation to discover the emptiness. `Explore` and the wallet link are the
 * two that always hold: there is always a hiive page, and there is always a
 * membership to look at.
 */
export function MemberNextStepsActions({
  hiiveName,
  hiiveSlug,
  nextSteps,
}: Props) {
  const tiles: Array<{
    key: string;
    href: string;
    icon: LucideIcon;
    label: string;
    detail: string;
  }> = [];

  if (nextSteps?.chat) {
    tiles.push({
      key: "chat",
      href: `/chat/${nextSteps.chat.id}`,
      icon: MessageCircle,
      label: "Open the chat",
      detail: nextSteps.chat.name || "Say hello",
    });
  }

  if ((nextSteps?.upcomingEventCount ?? 0) > 0) {
    const count = nextSteps?.upcomingEventCount ?? 0;
    tiles.push({
      key: "events",
      href: `/hiives/${hiiveSlug}?tab=events&focus_tabs=true`,
      icon: CalendarDays,
      label: "What's on",
      detail: plural(count, "event coming up", "events coming up"),
    });
  }

  if ((nextSteps?.venueCount ?? 0) > 0) {
    const count = nextSteps?.venueCount ?? 0;
    tiles.push({
      key: "venues",
      href: `/hiives/${hiiveSlug}?tab=venues&focus_tabs=true`,
      icon: MapPin,
      label: "Book a space",
      detail: plural(count, "venue", "venues"),
    });
  }

  if ((nextSteps?.memberCount ?? 0) > 0) {
    const count = nextSteps?.memberCount ?? 0;
    tiles.push({
      key: "members",
      href: `/hiives/${hiiveSlug}?tab=members&focus_tabs=true`,
      icon: Users,
      label: "Who else is here",
      detail: plural(count, "member", "members"),
    });
  }

  return (
    <div className="flex w-full flex-col gap-4 text-left">
      {tiles.length > 0 ? (
        <ul className="grid gap-2.5 sm:grid-cols-2">
          {tiles.map((tile) => (
            <li key={tile.key}>
              <Link
                href={tile.href}
                className="flex h-full items-center gap-3 rounded-2xl border border-glass-border-08 bg-glass-bg-02 px-3.5 py-3 transition-colors duration-200 ease-out-strong pointer-fine:hover:border-glass-border-20 pointer-fine:hover:bg-glass-bg-06"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand/12 text-brand">
                  <tile.icon className="size-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-text-primary">
                    {tile.label}
                  </span>
                  <span className="block truncate text-xs text-text-tertiary">
                    {tile.detail}
                  </span>
                </span>
                <ArrowRight className="size-4 shrink-0 text-text-tertiary" />
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-col gap-2">
        <Link
          href={`/hiives/${hiiveSlug}`}
          className="flex h-11 w-full items-center justify-center gap-1.5 rounded-2xl bg-brand text-sm font-semibold text-text-inverse transition-opacity duration-200 ease-out-strong pointer-fine:hover:opacity-90"
          style={{ backgroundColor: "var(--color-brand)" }}
        >
          <span className="truncate">Explore {hiiveName}</span>
          <ArrowRight className="size-4 shrink-0" aria-hidden />
        </Link>
        <Link
          href={`/wallet/hiives#hiive-${hiiveSlug}`}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl text-sm text-text-secondary transition-colors duration-200 ease-out-strong pointer-fine:hover:text-text-primary"
        >
          <Ticket className="size-4" aria-hidden />
          View your membership
        </Link>
      </div>
    </div>
  );
}
